import { BrokerPortfolioTypesEnum } from "dash-common";
import {
  Button,
  Form,
  FormContext,
  FormProvider,
  Icon,
  Modal,
  ModalContext,
  Spinner,
  Text,
  toast,
  Toggle,
  useScreenResolution,
} from "dash-ui";
import {
  BrokerPortfolioStatusesEnum,
  GetAllFilesInPropertyDocument,
  GetPortfolioFolderByIdDocument,
  GetPortfolioMediaKitItemsDocument,
  Portfolio,
  useAddAllMarketingFilesToPortfolioMutation,
  useChangePortfolioStatusMutation,
  useGetPortfolioFolderByIdQuery,
  useRemoveAllMarketingFilesFromPortfolioMutation,
  useSetPortfolioMediaKitMediaMutation,
} from "gql/generated.graphql";
import { ChangeEvent, FormEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import MediaKit from "../SharePortfolioForm/components/MediaKit/MediaKit";
import {
  ManageMediaKitFormFields,
  ManageMediaKitFormFieldsList,
} from "./ManageMediaKitModal.constants";
import ConfirmMediaKitChangesModal from "./components/ConfirmMediaKitChangesModal";
import { WarningBanner } from "pages/ProfilePage/components/ProfileBilling/components";

export default function ManageMediaKitModal() {
  const { t } = useTranslation();
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);

  const isOpen = isOpenCurrent["ManageMediaKitModal"];
  const { portfolioId } = payload || {};

  const { data, loading } = useGetPortfolioFolderByIdQuery({
    skip: !portfolioId,
    variables: {
      portfolioFolderId: portfolioId,
    },
  });

  const portfolio = data?.getPortfolioFolderById as Portfolio;

  const onClose = () => {
    toggleModal(false, "ManageMediaKitModal");
  };

  return (
    <Modal
      isOpen={isOpen}
      w="90%"
      maxWidth={1024}
      onRequestClose={onClose}
      viewHierarchy={3}
      isCentered
      overflow={isMobile ? "auto" : "hidden"}
      title={t("pages:mediaKit.editTitle") || ""}>
      <FormProvider fieldsList={ManageMediaKitFormFieldsList}>
        {loading && (
          <div className="flex w-full h-32 justify-center items-center">
            <Spinner />
          </div>
        )}

        {/* ManageMediaKitForm is needed in order to use values from FormContext */}
        {!loading && portfolio && <ManageMediaKitForm portfolio={portfolio} />}
      </FormProvider>
    </Modal>
  );
}

function ManageMediaKitForm({ portfolio }: { portfolio: Portfolio }) {
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { toggleModal, payload, setPayload } = useContext(ModalContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const { formState, formOnSubmit, formOnSuccess, stateHandlers } =
    useContext(FormContext);
  const [isMediaKitEnabled, setIsMediaKitEnabled] = useState(
    !!portfolio.isAddedMediaKit
  );

  const [setMediaKit, { loading: setMediaKitLoading }] =
    useSetPortfolioMediaKitMediaMutation({
      refetchQueries: [
        GetPortfolioMediaKitItemsDocument,
        GetAllFilesInPropertyDocument,
        GetPortfolioFolderByIdDocument,
      ],
    });
  const [changePortfolioStatus, { loading: changeStatusLoading }] =
    useChangePortfolioStatusMutation();
  const [addAllMediaFiles, { loading: isAddAllLoading }] =
    useAddAllMarketingFilesToPortfolioMutation();
  const [removeAllFiles, { loading: isRemoveAllLoading }] =
    useRemoveAllMarketingFilesFromPortfolioMutation();

  const loading =
    setMediaKitLoading ||
    changeStatusLoading ||
    isAddAllLoading ||
    isRemoveAllLoading;

  const unsavedChanges =
    formState[ManageMediaKitFormFields.AddedMedia]?.some(
      (state: { portfolioId: string; mediaIds: any[] }) =>
        state.mediaIds.length > 0
    ) ||
    formState[ManageMediaKitFormFields.RemovedMedia]?.some(
      (state: { portfolioId: string; mediaIds: any[] }) =>
        state.mediaIds.length > 0
    ) ||
    isMediaKitEnabled !== portfolio.isAddedMediaKit;

  async function resetAddedAndRemovedMedia() {
    stateHandlers[ManageMediaKitFormFields.AddedMedia]([
      {
        portfolioId: formState[ManageMediaKitFormFields.CurrentPortfolioId],
        mediaIds: [],
      },
    ]);
    stateHandlers[ManageMediaKitFormFields.RemovedMedia]([
      {
        portfolioId: formState[ManageMediaKitFormFields.CurrentPortfolioId],
        mediaIds: [],
      },
    ]);
  }

  async function handleBulkAction({ mode }: { mode: "add" | "remove" }) {
    setPayload({
      ...payload,
      onSubmit: () => {
        confirmBulkAction({ mode });
      },
      loading,
      mode,
    });
    toggleModal(true, "ConfirmMediaKitChangesModal");
  }

  async function confirmBulkAction({ mode }: { mode: "add" | "remove" }) {
    try {
      const bulkAction = mode === "add" ? addAllMediaFiles : removeAllFiles;

      const currentPortfolioItemId =
        formState[ManageMediaKitFormFields.CurrentPortfolioId];

      await bulkAction({
        refetchQueries: [
          GetPortfolioMediaKitItemsDocument,
          GetAllFilesInPropertyDocument,
          GetPortfolioFolderByIdDocument,
        ],
        variables: {
          portfolioItemId: currentPortfolioItemId,
        },
      });

      resetAddedAndRemovedMedia();

      toast.success("Media kit updated sucessfully!");
    } catch (err) {
      toast.error("There was an error updating media kit settings.");
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    formOnSubmit();

    try {
      await changePortfolioStatus({
        variables: {
          status: portfolio.status as BrokerPortfolioStatusesEnum,
          portfolioId: portfolio._id,
          isAddedMediaKit: isMediaKitEnabled,
        },
      });

      await setMediaKit({
        variables: {
          input: {
            addMediaKitItems:
              formState.addedMedia?.map((item: any) => ({
                portfolioId: item.portfolioId,
                mediaIdsToAdd: item.mediaIds.map((media: any) => media._id),
              })) || [],
            removeMediaKitItems:
              formState.removedMedia?.map((item: any) => ({
                portfolioId: item.portfolioId,
                mediaIdsToRemove: item.mediaIds.map((media: any) => media._id),
              })) || [],
          },
        },
      });

      resetAddedAndRemovedMedia();

      formOnSuccess();
      toast.success("Media kit updated sucessfully!");
    } catch (err) {
      toast.error("There was an error updating media kit settings.");
    }
  }

  useEffect(() => {
    setIsMediaKitEnabled(isMediaKitEnabled);
  }, [portfolio.isAddedMediaKit]);


  return (
    <Form onSubmit={handleSubmit}>
      <div className="w-full flex flex-col justify-center">
        <p className="text-slate-600 text-xs mb-2">
          DASH Downloads lets you easily share marketing files with your
          customers directly in your portfolio's property pages.
        </p>
        {unsavedChanges && (
          <WarningBanner className="flex flex-row gap-2 justify-center items-center mb-4">
            <Icon name="warningFilled" /> You have unsaved changes!
          </WarningBanner>
        )}
        <div className="flex flex-row justify-center border rounded-lg border-slate-300 py-2 px-4 w-full">
          <Toggle
            checked={isMediaKitEnabled}
            disabled={loading}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setIsMediaKitEnabled(event.target.checked);
            }}
          />
          <div className="ml-4">
            <Text whiteSpace="nowrap" color={theme.colors.n80}>
              {t("pages:mediaKit.enableDashDownloads")}
            </Text>
          </div>
        </div>
      </div>
      {isMediaKitEnabled && (
        <MediaKit
          compactMediaList={isMobile}
          isEdit
          onBulkAction={handleBulkAction}
          isLoading={loading}
          portfolioFolderId={portfolio._id}
          isFolder={portfolio.type === BrokerPortfolioTypesEnum.FOLDER}
        />
      )}
      <div className="flex flex-row justify-center gap-2 mt-2">
        <Button isLoading={loading} variation="primary" type="submit">
          Save
        </Button>
        <Button
          variation="outline"
          type="button"
          onClick={() => {
            toggleModal(false, "ManageMediaKitModal");
          }}>
          Cancel
        </Button>
      </div>
      <ConfirmMediaKitChangesModal />
    </Form>
  );
}
